import { Controller } from "@hotwired/stimulus"

// Cloudflare Turnstile Captcha Stimulus Controller
// Documentation: https://developers.cloudflare.com/turnstile/
//
// Usage:
// <button data-controller="turnstile-captcha"
//         data-turnstile-captcha-site-key-value="your_site_key"
//         data-action="click->turnstile-captcha#verify">
//   Verify
// </button>
//
// Events:
// - captcha:verified - dispatched when verification succeeds, detail: { ticket, randstr }
// - captcha:error - dispatched when verification fails or user cancels

export default class extends Controller {
  static values = {
    siteKey: String
  }

  connect() {
    this.loadScript()
    this.widgetId = null
    this.overlayEl = null
  }

  disconnect() {
    this.removeOverlay()
  }

  loadScript() {
    if (window.turnstile) return Promise.resolve()
    if (this.constructor.scriptPromise) return this.constructor.scriptPromise

    this.constructor.scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Captcha script failed to load'))
      document.head.appendChild(script)
    })

    return this.constructor.scriptPromise
  }

  verify(event) {
    event.preventDefault()

    if (!this.siteKeyValue) {
      console.error('Turnstile siteKey not configured')
      this.dispatchError('not_configured', 'Captcha not configured')
      return
    }

    this.loadScript()
      .then(() => {
        if (!window.turnstile) {
          console.error('Turnstile script not loaded')
          this.dispatchError('script_not_loaded', 'Captcha script not loaded')
          return
        }

        this.showOverlay()
      })
      .catch((error) => {
        console.error('Turnstile script failed to load', error)
        this.dispatchError('script_load_failed', error.message || 'Captcha script failed to load')
      })
  }

  showOverlay() {
    // Create overlay for modal-like experience
    this.overlayEl = document.createElement('div')
    this.overlayEl.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50'
    this.overlayEl.innerHTML = `
      <div class="bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-xl max-w-sm mx-4">
        <div class="text-center mb-4">
          <p class="text-sm text-zinc-600 dark:text-zinc-300">Please complete verification</p>
        </div>
        <div id="turnstile-widget" class="flex justify-center"></div>
        <button type="button" class="mt-4 w-full text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200">
          Cancel
        </button>
      </div>
    `

    // Add cancel handler
    this.overlayEl.querySelector('button').addEventListener('click', () => {
      this.removeOverlay()
      this.dispatchError('user_cancelled', 'User cancelled verification')
    })

    document.body.appendChild(this.overlayEl)

    // Render Turnstile widget
    try {
      this.widgetId = window.turnstile.render('#turnstile-widget', {
        sitekey: this.siteKeyValue,
        callback: (token) => this.handleSuccess(token),
        'error-callback': (error) => this.handleError(error),
        'expired-callback': () => this.handleExpired(),
        'timeout-callback': () => this.handleTimeout()
      })
    } catch (e) {
      this.removeOverlay()
      this.dispatchError('render_failed', e.message || 'Failed to render verification')
    }
  }

  removeOverlay() {
    if (this.overlayEl) {
      if (this.widgetId != null && window.turnstile) {
        try {
          window.turnstile.remove(this.widgetId)
        } catch (e) {
          // Widget might already be removed
        }
        this.widgetId = null
      }
      this.overlayEl.remove()
      this.overlayEl = null
    }
  }

  handleSuccess(token) {
    this.removeOverlay()
    // Turnstile uses single token, we pass empty randstr for compatibility
    this.dispatchVerified(token, '')
  }

  handleError(error) {
    this.removeOverlay()
    this.dispatchError('turnstile_error', error || 'Verification failed')
  }

  handleExpired() {
    this.removeOverlay()
    this.dispatchError('token_expired', 'Token expired, please retry')
  }

  handleTimeout() {
    this.removeOverlay()
    this.dispatchError('timeout', 'Verification timed out')
  }

  dispatchVerified(ticket, randstr) {
    const event = new CustomEvent('captcha:verified', {
      bubbles: true,
      detail: { ticket, randstr }
    })
    this.element.dispatchEvent(event)
  }

  dispatchError(errorCode, errorMessage) {
    const event = new CustomEvent('captcha:error', {
      bubbles: true,
      detail: { errorCode, errorMessage }
    })
    this.element.dispatchEvent(event)
  }
}
